<template>
  <div>
    <el-dialog
      v-model="ApplyForTraining"
      title="滑块验证"
      center
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      @closed="handleDialogClosed"
      width="350px"
      style="position: fixed;top: 18vh;left: 50%;right: 50%;transform: translate(-50%,-50%);"
    >
      <div style="padding: 10px 0" v-loading="showLoading">
        <div class="drag-container" ref="dragContainerRef">
          <div
            class="verify-bg"
            :style="{
              backgroundImage: generateData.backgroundImage
                ? `url('${generateData.backgroundImage}')`
                : 'none',
            }"
          ></div>
          <!-- 优化 1: 使用 div 包裹图片，避免图片拖拽的原生行为干扰 -->
          <div
            v-if="generateData.sliderImage"
            class="verify-slider-wrapper"
            :style="{ left: sliderLeft + 'px' }"
            @mousedown="handleDragStart"
            @touchstart.prevent="handleDragStart"
          >
            <img
              :src="generateData.sliderImage"
              class="verify-slider-img"
              alt="滑块"
            />
          </div>
          <el-icon
            size="16"
            style="
              position: absolute;
              right: 10px;
              top: 10px;
              z-index: 666;
              cursor: pointer;
            "
            color="#fff"
            @click.stop="resetAndGenerate"
          >
            <RefreshLeft />
          </el-icon>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { RefreshLeft } from '@element-plus/icons-vue'
import { drapCaptcha, generates } from '@/api/DragAndDropVerification'

const emit = defineEmits(['update', 'verified'])
const props = defineProps({
  DragShow: {
    type: Boolean,
    default: false,
  },
})

const ApplyForTraining = ref(false)
const generateData = ref({
  backgroundImage: '',
  sliderImage: '',
})
const generateCode = ref('')
const sliderLeft = ref(0)
const isDragging = ref(false)
const dragContainerRef = ref(null)
// 动态获取容器尺寸，避免硬编码
const containerWidth = ref(300)
const sliderWidth = ref(40)
const verifyData = ref('')
const showLoading = ref(false)

// 重置所有状态
function resetAllState() {
  sliderLeft.value = 0
  isDragging.value = false
  generateData.value = { backgroundImage: '', sliderImage: '' }
  generateCode.value = ''
  verifyData.value = ''
}

// 重置并生成验证码
function resetAndGenerate() {
  resetAllState()
  generateFun()
}

// 动态获取容器尺寸
onMounted(() => {
  if (dragContainerRef.value) {
    containerWidth.value = dragContainerRef.value.offsetWidth || 300
  }
})

// 监听弹窗显隐
watch(
  () => props.DragShow,
  newVal => {
    ApplyForTraining.value = newVal
    if (newVal) {
      resetAllState()
      // 弹窗显示后，确保能获取到最新的容器尺寸
      nextTick(() => {
        if (dragContainerRef.value) {
          containerWidth.value = dragContainerRef.value.offsetWidth || 300
        }
        resetAndGenerate()
      })
    }
  },
  { immediate: true }
)

// 重构拖拽开始逻辑
function handleDragStart(e) {
  if (!generateData.value.sliderImage || isDragging.value) return

  isDragging.value = true

  // 阻止浏览器默认行为，如选中文本、拖拽图片等
  e.preventDefault()

  // 区分鼠标和触摸事件
  const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX

  // 计算鼠标/手指相对于滑块左侧的位置
  const startX = clientX - sliderLeft.value

  //  动态绑定和移除事件，避免内存泄漏
  const handleDragMove = moveEvent => {
    if (!isDragging.value) return

    moveEvent.preventDefault()
    const currentClientX =
      moveEvent.type === 'touchmove'
        ? moveEvent.touches[0].clientX
        : moveEvent.clientX
    let newLeft = currentClientX - startX

    // 边界限制
    newLeft = Math.max(0, newLeft)
    newLeft = Math.min(newLeft, containerWidth.value - sliderWidth.value)

    sliderLeft.value = newLeft
  }

  const handleDragEnd = () => {
    if (!isDragging.value) return

    isDragging.value = false

    // 移除全局事件监听
    document.removeEventListener('mousemove', handleDragMove)
    document.removeEventListener('touchmove', handleDragMove)
    document.removeEventListener('mouseup', handleDragEnd)
    document.removeEventListener('touchend', handleDragEnd)

    // 执行验证逻辑
    verifySliderPosition()
  }

  // 绑定全局事件监听
  document.addEventListener('mousemove', handleDragMove)
  document.addEventListener('touchmove', handleDragMove, { passive: false }) // passive: false 以便能 preventDefault
  document.addEventListener('mouseup', handleDragEnd)
  document.addEventListener('touchend', handleDragEnd)
}

// 验证滑块位置
function verifySliderPosition() {
  console.log('滑块最终位置：', sliderLeft.value + 'px')

  if (!generateCode.value?.captchaId) {
    ElMessage.error('验证码未加载完成，请刷新重试')
    resetAndGenerate()
    return
  }

  const verifyParams = {
    captchaId: generateCode.value.captchaId,
    position: sliderLeft.value,
  }

  drapCaptcha(verifyParams)
    .then(res => {
      console.log('验证接口返回结果：', res)
      if (res.data.success) {
        verifyData.value = res.data
        ElMessage.success('验证成功')
        ApplyForTraining.value = false
        // 优化 7: 使用新的 emit 方式
        emit('verified', res.data.token || '')
      } else {
        ElMessage.error('验证失败，请重新验证')
        setTimeout(() => {
          resetAndGenerate()
        }, 1500)
      }
    })
    .catch(err => {
      console.error('验证接口异常：', err)
      ElMessage.error('验证异常，请稍后重试')
      ApplyForTraining.value = false
    })
}

// 弹窗关闭回调
function handleDialogClosed() {
  // 优化 7: 遵循 Vue 3 的 v-model:prop 写法
  emit('update', {
    DragShow: false,
    token: verifyData.value.token || ''
  })
  resetAllState()
}

// 生成验证码
function generateFun() {
  showLoading.value = true
  generates()
    .then(res => {
      if (res.code === 1000 && res.data) {
        generateCode.value = res.data
        generateData.value = {
          backgroundImage: `data:image/png;base64,${res.data.backgroundImage}`,
          sliderImage: `data:image/png;base64,${res.data.sliderImage}`,
        }
      } else {
        ElMessage.error('验证码加载失败，请刷新重试')
        generateData.value = { backgroundImage: '', sliderImage: '' }
      }
      showLoading.value = false
    })
    .catch(err => {
      console.error('验证码接口异常：', err)
      ElMessage.error('验证码加载异常，请稍后重试')
      generateData.value = { backgroundImage: '', sliderImage: '' }
      showLoading.value = false
    })
}
</script>

<style scoped lang="scss">
.drag-container {
  position: relative;
  width: 300px;
  height: 150px;
  margin: 0 auto;
  box-sizing: border-box;
  // 优化 5: 禁止用户选择文本，防止拖拽时选中文本
  user-select: none;
  -webkit-user-select: none;
}

.verify-bg {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f5f5f5;
  border-radius: 4px;
  box-sizing: border-box;
}

// 优化 1 & 5: 重构滑块样式，提升性能
.verify-slider-wrapper {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  cursor: grab;
  z-index: 10;
  height: 60px;
  // 核心优化：开启硬件加速，让动画更流畅
  will-change: left;
  transform: translateY(-50%) translateZ(0);

  &:active {
    cursor: grabbing;
  }
}

.verify-slider-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  // 禁止图片被拖拽
  user-select: none;
  -webkit-user-drag: none;
  // 让鼠标事件穿透图片，直接作用于父容器
  pointer-events: none;
}
</style>